import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocationClients, useRefreshClients } from '../../../features/locations/use-clients'
import { useLocation } from '../../../features/locations/use-location'
import { useIdOrCode } from '../../use-id-or-code'
import { ActionRow, Btn, Inline, PageHeader, Tag, th, utc } from './controls'
import { mono, Note, Section, SkeletonRows, serif } from './ui'

function useElapsed(active: boolean): number {
  const [s, setS] = useState(0)
  useEffect(() => {
    if (!active) {
      setS(0)
      return
    }
    const t = setInterval(() => setS((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [active])
  return s
}

export function LedgerClients() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const [noIpOnly, setNoIpOnly] = useState(false)
  const clients = useLocationClients(idOrCode, { noIpOnly })
  const refresh = useRefreshClients()
  const elapsed = useElapsed(refresh.isPending)

  const header = (
    <PageHeader
      idOrCode={idOrCode}
      section="Network"
      page="Clients"
      title="Clients"
      standfirst="Every machine seen on the store network, with the address it is using, if any."
    />
  )

  if (clients.isLoading || location.isLoading)
    return (
      <article>
        {header}
        <SkeletonRows />
      </article>
    )
  if (clients.error || !clients.data)
    return (
      <article>
        {header}
        <Note tone="error">
          The clients could not be read: {clients.error ? apiErrorMessage(clients.error) : 'no data.'}
        </Note>
      </article>
    )

  const { clients: rows, lastRefreshedAt, isStale } = clients.data
  const aptosCode = location.data?.aptosStoreCode ?? location.data?.code ?? idOrCode

  return (
    <article>
      {header}
      <ActionRow>
        <Btn variant="primary" disabled={refresh.isPending} onClick={() => refresh.mutate({ aptosCodes: [aptosCode] })}>
          {refresh.isPending ? `Collecting… ${elapsed}s` : 'Refresh clients'}
        </Btn>
        <label className="flex cursor-pointer items-center gap-2 text-[13.5px]">
          <input
            type="checkbox"
            className="accent-[var(--color-accent)]"
            checked={noIpOnly}
            onChange={(e) => setNoIpOnly(e.target.checked)}
          />
          Without an IP address only
        </label>
        <span className="ml-auto flex items-center gap-3 text-[14px] italic text-[var(--color-muted)]" style={serif}>
          Last refreshed {utc(lastRefreshedAt)}
          {isStale ? <Tag tone="warn">Stale</Tag> : null}
        </span>
      </ActionRow>
      <div aria-live="polite" className="grid gap-1">
        {refresh.isPending ? (
          <Inline tone="muted">Collecting from the network; this usually takes under a minute.</Inline>
        ) : null}
        {refresh.error ? <Inline tone="bad">{apiErrorMessage(refresh.error)}</Inline> : null}
        {refresh.data?.status === 'complete' ? <Inline tone="ok">Refreshed.</Inline> : null}
        {refresh.data?.status === 'running' ? (
          <Inline tone="muted">Still collecting; check back shortly.</Inline>
        ) : null}
        {refresh.data && (refresh.data.status === 'errored' || refresh.data.status === 'terminated') ? (
          <Inline tone="bad">
            The refresh {refresh.data.status}
            {refresh.data.error ? `: ${refresh.data.error}` : '.'}
          </Inline>
        ) : null}
      </div>

      <Section
        title="Seen on the network"
        aside={<span className="tabular-nums text-[var(--color-muted)]">{rows.length} rows</span>}
      >
        {rows.length === 0 ? (
          <Note>{noIpOnly ? 'Every client has an IP address.' : 'No clients have been seen yet.'}</Note>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse" aria-label="Clients">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {['Hostname', 'MAC', 'IP', 'VLAN', 'Vendor', 'Seen in', 'Last seen'].map((h) => (
                    <th key={h} scope="col" className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.mac} className="h-10 border-b border-[var(--color-border)]">
                    <td className="pr-4 text-[15px]" style={serif}>
                      {c.hostname ?? '—'}
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {c.mac}
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {c.ip ?? <Tag tone="warn">No IP</Tag>}
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {c.vlan ?? '—'}
                    </td>
                    <td className="pr-4 text-[14px] text-[var(--color-muted)]">{c.vendor || '—'}</td>
                    <td className="pr-4 text-[12px] text-[var(--color-muted)]" style={mono}>
                      {c.seenIn.join(', ') || '—'}
                    </td>
                    <td className="pr-4 text-[13px] italic text-[var(--color-muted)]" style={serif}>
                      {utc(c.lastSeenAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </article>
  )
}
